import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FileSelectionButton } from '../../../../file-selection-button/file-selection-button';
import { Icon } from '../../../../icon/icon';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import { Stack } from '../../../../stack/stack';
import { TextBox } from '../../../../text-box/text-box';

export type TitleBlockValues = {
  title: string;
  description: string;
  responsible: string;
  freeText: string;
  uploadedImage?: {
    name: string;
    dataUrl: string;
  };
};

type Props = {
  values: TitleBlockValues;
  onChange: (values: Partial<TitleBlockValues>) => void;
};

/**
 * The logo and the text fields of the CAD drawing report's title block.
 */
export const CadDrawingTitleBlockFields = ({ values, onChange }: Props) => {
  const { t } = useTranslation();

  return (
    <>
      <Stack direction="column" spacing={0.5}>
        <FileSelectionButton
          accept="image/png,image/jpeg"
          color="secondary"
          leftIcon={{ icon: 'upload' }}
          multiple={false}
          text={t('cadDrawingReport.pdf.rightPanel.uploadLogo', { ns: 'skyviewReport' })}
          variant="contained"
          onFilesSelected={(files) => {
            const file = files?.[0];
            if (file) {
              const reader = new FileReader();
              reader.onload = (event) => {
                const dataUrl = event.target?.result as string;
                onChange({
                  uploadedImage: {
                    name: file.name,
                    dataUrl: dataUrl,
                  },
                });
              };
              reader.readAsDataURL(file);
            }
          }}
        />
        {values.uploadedImage && (
          <Stack alignItems="baseline" direction="row" spacing={0.5}>
            <FileName>{values.uploadedImage.name}</FileName>
            <Icon icon={['fad', 'trash']} onClick={() => onChange({ uploadedImage: undefined })} />
          </Stack>
        )}
      </Stack>

      <LabelledContainer text={t('cadDrawingReport.pdf.rightPanel.title', { ns: 'skyviewReport' })}>
        {(getId) => (
          <TextBox
            id={getId()}
            value={values.title}
            onChange={(e) => onChange({ title: e.target.value })}
          />
        )}
      </LabelledContainer>

      <LabelledContainer
        text={t('cadDrawingReport.pdf.rightPanel.description', { ns: 'skyviewReport' })}
      >
        {(getId) => (
          <TextBox
            id={getId()}
            value={values.description}
            onChange={(e) => onChange({ description: e.target.value })}
          />
        )}
      </LabelledContainer>

      <LabelledContainer
        text={t('cadDrawingReport.pdf.rightPanel.responsible', { ns: 'skyviewReport' })}
      >
        {(getId) => (
          <TextBox
            id={getId()}
            value={values.responsible}
            onChange={(e) => onChange({ responsible: e.target.value })}
          />
        )}
      </LabelledContainer>

      <LabelledContainer
        text={t('cadDrawingReport.pdf.rightPanel.freeText', { ns: 'skyviewReport' })}
      >
        {(getId) => (
          <TextBox
            id={getId()}
            value={values.freeText}
            onChange={(e) => onChange({ freeText: e.target.value })}
          />
        )}
      </LabelledContainer>
    </>
  );
};

const FileName = styled.span`
  font-size: 12px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
